<template>
  <v-card class="vendedores-section" elevation="4">
    <v-progress-linear :active="carregando" indeterminate absolute color="primary" />

    <!-- Header Melhorado -->
    <v-card-title class="section-header px-5 py-4">
      <div class="d-flex align-center flex-grow-1">
        <v-icon large color="primary" class="mr-3">mdi-account-group</v-icon>
        <div>
          <h2 class="text-h5 font-weight-bold primary--text mb-1">
            Meta x Venda Realizada por Vendedor
          </h2>
          <p class="text-caption grey--text text--darken-1 mb-0">
            Desempenho individual da equipe de vendas
          </p>
        </div>
      </div>

      <v-spacer class="d-none d-lg-block" />

      <div class="header-actions d-flex align-center flex-wrap">
        <v-img
            class="mx-3 d-none d-md-block"
            max-width="180"
            src="../../assets/icons/logo_dark_min.png"
            contain
        />
        <v-btn
            :disabled="carregando"
            color="primary"
            elevation="0"
            class="action-button"
            @click="$emit('ver-detalhes')"
        >
          <v-icon left size="18">mdi-magnify-plus-outline</v-icon>
          <span class="d-none d-sm-inline">Ver Detalhes</span>
          <span class="d-inline d-sm-none">Detalhes</span>
        </v-btn>
      </div>
    </v-card-title>

    <v-divider />

    <!-- Loading State -->
    <v-card-text v-if="carregando" class="py-12">
      <v-row>
        <v-col
            v-for="n in 3"
            :key="n"
            cols="12"
            sm="6"
            md="4"
        >
          <v-skeleton-loader
              type="card"
              elevation="2"
          />
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Content -->
    <v-card-text v-else class="pa-5">
      <v-fade-transition mode="out-in">
        <!-- Cards de Vendedores (mantidos iguais) -->
        <div v-if="temDados" class="vendedores-cards-container">
          <v-row>
            <v-col
                v-for="(vendedor, index) in vendedoresData"
                :key="index"
                cols="12"
                sm="6"
                md="4"
                class="d-flex "
            >
              <v-card
                  class="vendedor-card flex-grow-1"
                  :class="getCardClass(vendedor.atingimento)"
                  elevation="1"
              >
                <v-card-title class="pb-2">
                  <v-avatar :color="getColorByPerformance(vendedor.atingimento)" size="40" class="mr-3">
                    <span class="white--text font-weight-bold">{{ vendedor.nome.charAt(0) }}</span>
                  </v-avatar>
                  <div class="flex-grow-1">
                    <div class="text-subtitle-1 font-weight-bold text-truncate">
                      {{ vendedor.nome }}
                    </div>
                    <div class="text-caption grey--text">
                      Vendedor
                    </div>
                  </div>
                </v-card-title>

                <v-card-text>
                  <div class="mb-3">
                    <div class="d-flex justify-space-between mb-1">
                      <span class="text-caption grey--text text--darken-1">Meta</span>
                      <span class="text-body-2 font-weight-bold">{{ formataDinheiro(vendedor.meta) }}</span>
                    </div>
                    <div class="d-flex justify-space-between mb-1">
                      <span class="text-caption grey--text text--darken-1">Realizado</span>
                      <span class="text-body-2 font-weight-bold primary--text">{{ formataDinheiro(vendedor.realizado) }}</span>
                    </div>
                    <v-divider class="my-2"></v-divider>
                    <div class="d-flex justify-space-between align-center">
                      <span class="text-caption grey--text text--darken-1">Atingimento</span>
                      <v-chip
                          small
                          :color="getColorByPerformance(vendedor.atingimento)"
                          dark
                          class="font-weight-bold"
                      >
                        {{ vendedor.atingimento.toFixed(1) }}%
                      </v-chip>
                    </div>
                  </div>

                  <v-progress-linear
                      :value="vendedor.atingimento"
                      :color="getColorByPerformance(vendedor.atingimento)"
                      height="8"
                      rounded
                      class="mb-2"
                  ></v-progress-linear>

                  <div class="text-center" v-if="false">
                    <span class="text-caption">
                      Faltam {{ formataDinheiro(Math.max(0, vendedor.meta - vendedor.realizado)) }}
                    </span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Empty State Melhorado -->
        <div v-else class="empty-state py-12">
          <v-icon size="80" color="grey lighten-1" class="mb-4">
            mdi-account-search
          </v-icon>
          <p class="text-h6 grey--text text--darken-1 mb-2">
            Nenhum vendedor encontrado
          </p>
          <p class="text-caption grey--text mb-0">
            Ajuste os filtros de período para visualizar os dados
          </p>
        </div>
      </v-fade-transition>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "CardsVendedoresMaquinaVendas",

  props: {
    dataSeries: {
      type: Array,
      default: () => []
    },
    labels: {
      type: Array,
      default: () => []
    },
    carregando: {
      type: Boolean,
      default: false
    },
  },

  computed: {
    vendedoresData() {
      if (!this.dataSeries || !this.dataSeries.length || !this.labels || !this.labels.length) {
        return [];
      }

      return this.labels.map((nome, index) => ({
        nome,
        meta: this.dataSeries[0]?.data[index] || 0,
        realizado: this.dataSeries[1]?.data[index] || 0,
        atingimento: this.dataSeries[2]?.data[index] || 0,
        margem: this.dataSeries[3]?.data?.[index] || null,
      }));
    },

    temDados() {
      return this.vendedoresData.length > 0;
    }
  },

  methods: {
    formataDinheiro(valor) {
      if (!valor) return 'R$ 0,00';
      return parseFloat(valor).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      });
    },

    getColorByPerformance(atingimento) {
      if (atingimento >= 100) return '#4CAF50'; // Verde - Meta atingida
      if (atingimento >= 80) return '#FFC107'; // Amarelo - Próximo da meta
      if (atingimento >= 60) return '#FF9800'; // Laranja - Atenção
      return '#F44336'; // Vermelho - Abaixo do esperado
    },

    getCardClass(atingimento) {
      if (atingimento >= 100) return 'card-success';
      if (atingimento >= 80) return 'card-warning';
      return 'card-default';
    },
  },
};
</script>

<style scoped>
/* Card Pai */
.vendedores-section {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

/* Header Melhorado */
.section-header {
  background: linear-gradient(to right, #f8f9fa 0%, #ffffff 100%);
  border-bottom: 1px solid #e0e0e0;
  min-height: 80px;
}

.header-actions {
  gap: 16px;
}

.action-button {
  height: 40px !important;
  padding: 0 20px !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  font-weight: 600 !important;
}

/* Container de Cards */
.vendedores-cards-container {
  padding: 8px 0;
}

/* Cards dos Vendedores (mantidos iguais) */
.vendedor-card {
  transition: all 0.3s ease;
  border-top: 4px solid transparent;
  overflow: hidden;
  width: 100%;
}

.vendedor-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
}

.card-success {
  border-top-color: #4CAF50;
}

.card-warning {
  border-top-color: #FFC107;
}

.card-default {
  border-top-color: #1E3A5F;
}

/* Empty State Melhorado */
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

/* Transições */
.v-fade-transition {
  transition: opacity 0.3s ease;
}

/* Responsivo */
@media (max-width: 960px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 16px;
    min-height: auto;
    padding: 20px !important;
  }

  .header-actions {
    width: 100%;
    justify-content: space-between;
  }
}

@media (max-width: 600px) {
  .section-header {
    padding: 16px !important;
  }

  .action-button {
    height: 36px !important;
    padding: 0 16px !important;
    font-size: 13px !important;
  }
}
</style>